<template>
  <c-box marginTop="2xl" data-cy="eventsBannerHeader">
    <c-heading as="h1" fontSize="4xl" fontWeight="normal" marginTop="4xl" marginBottom="2xl">
      {{ $t("events.bannerHeader") }}
    </c-heading>

    <c-flex
      mx="-15px"
      justifyContent="space-between"
      :flexDirection="{ base: 'column', sm: 'row' }"
      flexWrap="wrap"
    >
      <c-flex
        justifyContent="flex-start"
        :maxWidth="{ base: '100%', sm: '83.33333333%' }"
        alignItems="center"
        px="15px"
        mb="xs"
      >
        <c-button-group
          display="flex"
          flex-wrap="nowrap"
          width="100%"
          justifyContent="space-between"
        >
          <c-button
            :background="filters.category === undefined ? 'primary' : 'transparent'"
            :color="filters.category === undefined ? 'white' : 'primary'"
            :borderColor="filters.category === undefined ? 'primary' : 'transparent'"
            py="xl"
            marginRight="lg"
            @click="updateCategoryFilter(undefined)"
          >
            {{ $t("events.categoryFilter.all") }}
          </c-button>
          <c-button
            :background="filters.category === 'TRAINING' ? 'primary' : 'transparent'"
            :color="filters.category === 'TRAINING' ? 'white' : 'primary'"
            :borderColor="filters.category === 'TRAINING' ? 'primary' : 'transparent'"
            py="xl"
            marginRight="lg"
            data-cy="trainingCategory"
            @click="updateCategoryFilter(EventCategory.Training)"
          >
            {{ $t("events.categoryFilter.trainings") }}
          </c-button>
          <c-button
            :background="filters.category === 'EVENT' ? 'primary' : 'transparent'"
            :color="filters.category === 'EVENT' ? 'white' : 'primary'"
            :borderColor="filters.category === 'EVENT' ? 'primary' : 'transparent'"
            py="xl"
            data-cy="eventCategory"
            @click="updateCategoryFilter(EventCategory.Event)"
          >
            {{ $t("events.categoryFilter.events") }}
          </c-button>
        </c-button-group>
      </c-flex>
      <c-box px="15px">
        <c-select
          id="city"
          v-model="filters.city"
          @change="updateCityFilter(filters.city)"
          data-cy="citySelect"
          float="right"
          size="md"
          borderRadius="full"
          background="brand.grayLight"
          borderColor="brand.purple"
          :maxWidth="{ base: '100%', sm: '250px' }"
          :width="{ base: '100%', sm: 'auto' }"
        >
          <option key="all" :value="undefined">
            {{ filters.city ? $t("events.allCities") : $t("events.filterByCity") }}
          </option>
          <option v-for="city in cities" :key="city" :value="city">{{ city }}</option>
        </c-select>
      </c-box>
    </c-flex>

    <c-box>
      <c-box
        v-if="eventGroups.length === 0"
        p="3xl"
        my="4xl"
        backgroundColor="white"
        boxShadow="base"
        borderRadius="base"
      >
        <c-text>{{ $t("events.noEvents") }}</c-text>
      </c-box>
    </c-box>

    <c-flex
      v-for="eventGroup in eventGroups"
      :key="`${eventGroup.year}-${eventGroup.month}`"
      mx="-15px"
      flexWrap="wrap"
      data-cy="eventGroups"
    >
      <c-flex width="100%" px="15px">
        <c-heading
          as="h3"
          width="100%"
          fontWeight="400"
          fontSize="2xl"
          py="sm"
          my="xl"
          borderBottom="1px solid"
          borderColor="lightGray"
          >{{ $t(`events.month${eventGroup.month}`) }} {{ eventGroup.year }}</c-heading
        >
      </c-flex>
      <c-box
        v-for="event in eventGroup.events"
        :key="event.id"
        px="15px"
        width="100%"
        minHeight="200px"
        :maxWidth="{ sm: '50%', md: '33.333333%' }"
        data-cy="event"
      >
        <c-box
          backgroundColor="white"
          boxShadow="base"
          borderRadius="base"
          my="md"
          tabindex="0"
          @click="showEventDetails(event)"
          @keydown.enter="showEventDetails(event)"
          cursor="pointer"
        >
          <c-box
            :background="event.category == 'EVENT' ? 'brand.blue' : 'brand.beigeDarkest'"
            color="white"
            borderTopLeftRadius="base"
            borderTopRightRadius="base"
            data-cy="event-category"
            px="xl"
            py="2xs"
          >
            <c-text as="span" fontSize="xs">{{ $t(`events.categories.${event.category}`) }}</c-text>
          </c-box>
          <c-box p="xl">
            <c-box fontSize="sm" marginBottom="sm">
              <c-text as="span" paddingRight="2xs">{{ event.startTime | moment("DD.MM.") }}</c-text>
              <c-text as="span">{{ event.city }}</c-text>
            </c-box>
            <c-heading as="h4" marginBottom="md" fontWeight="400" fontSize="xl">{{
              event.name
            }}</c-heading>
            <c-box
              v-if="event.enrollmentOpenTime.getTime() > Date.now()"
              fontSize="sm"
              color="gray"
            >
              {{ $t("events.enrollmentOpens") }} {{ event.enrollmentOpenTime | moment("DD.MM.") }}
            </c-box>
            <c-box
              v-else-if="event.enrollmentDeadline.getTime() < Date.now()"
              fontSize="sm"
              color="gray"
            >
              {{ $t("events.enrollmentEnded") }}
            </c-box>
            <c-box v-else-if="event.participantCapacity === null" fontSize="sm" color="gray">
              {{ $t("events.enrollmentOpened") }}
            </c-box>
            <c-box
              v-else
              :title="$t('events.checkFromLyyti')"
              fontSize="sm"
              fontWeight="700"
              :color="
                event.participantCount >= event.participantCapacity
                  ? 'brand.redDark'
                  : 'brand.greenDark'
              "
            >
              <c-icon name="person" size="1.5rem" />
              {{ event.participantCount }}/{{ event.participantCapacity }}
              <c-text
                v-if="event.participantCount >= event.participantCapacity"
                as="span"
                fontSize="sm"
              >
                ({{ $t("events.full") }})
              </c-text>
            </c-box>
          </c-box>
        </c-box>
      </c-box>
    </c-flex>

    <dialog-modal
      v-if="selectedEvent"
      :show-dialog="!!selectedEvent"
      @close="hideEventDetails"
      content-ref="selected-event-dialog"
    >
      <template v-slot:title>
        <c-box
          :background="selectedEvent.category == 'EVENT' ? 'brand.blue' : 'brand.beigeDarkest'"
          color="white"
          borderTopRightRadius="base"
          borderBottomRightRadius="base"
          display="inline-block"
          :py="{ base: '2xs', md: 'xs' }"
          :px="{ base: 'md', md: 'lg' }"
          :pl="{ base: 'xl', md: '10' }"
          :mx="{ base: '-10', md: '-10' }"
        >
          <c-text as="span" fontSize="sm" fontWeight="400">{{
            $t(`events.categories.${selectedEvent.category}`)
          }}</c-text>
        </c-box>
      </template>
      <template v-slot:content>
        <c-heading as="h4" marginBottom="md" fontSize="xl">{{ selectedEvent.name }}</c-heading>
        <c-box data-cy="event-info">
          <c-box
            fontSize="sm"
            marginBottom="xl"
            paddingBottom="xl"
            borderBottom="1px solid"
            borderColor="black"
          >
            <c-text as="span">
              {{ selectedEvent.startTime | moment("DD.MM.YYYY HH:mm") }} &#8211;
              {{ selectedEvent.endTime | moment("DD.MM.YYYY HH:mm") }}
              <c-text as="span" v-if="!isClientTimezone(selectedEvent.timezone)">
                <c-icon name="alert" size="1.25rem" /> {{ $t("events.timeZoneDiffers") }}
              </c-text>
            </c-text>
            <c-text as="span">{{ selectedEvent.city }}</c-text>
          </c-box>
          <c-box fontSize="sm" marginBottom="sm">
            <c-text>
              {{ selectedEvent.location }}
            </c-text>
            <c-text>
              {{ selectedEvent.address }}
            </c-text>
          </c-box>
          <c-box fontSize="sm" marginBottom="sm">
            <c-text>
              {{ $t("events.enrollment") }}
              {{ selectedEvent.enrollmentOpenTime | moment("DD.MM.YYYY HH:mm") }} &#8211;
              {{ selectedEvent.enrollmentDeadline | moment("DD.MM.YYYY HH:mm") }}
            </c-text>
            <c-text
              v-if="
                selectedEvent.enrollmentOpenTime.getTime() < Date.now() &&
                selectedEvent.enrollmentDeadline.getTime() > Date.now()
              "
            >
              <c-text v-if="selectedEvent.participantCapacity === null" marginBottom="sm">
                {{ $t("events.enrolledCount") }} {{ selectedEvent.participantCount }} ({{
                  $t("events.noCapacityLimit")
                }})
              </c-text>
              <c-text v-else marginBottom="sm">
                {{ $t("events.freeCapacity") }}
                <c-text as="span" fontSize="md"
                  >{{ selectedEvent.participantCapacity - selectedEvent.participantCount }} /
                  {{ selectedEvent.participantCapacity }}</c-text
                >
              </c-text>
              <c-text marginBottom="sm">
                <c-icon name="alert" size="1.25rem" /> {{ $t("events.checkFromLyyti") }}
              </c-text>
            </c-text>
          </c-box>
          <c-box fontSize="sm" marginBottom="sm">
            <c-text>{{ $t("events.moreInfo") }}</c-text>
          </c-box>
        </c-box>
      </template>
      <template v-slot:actions>
        <c-button @click="hideEventDetails" data-cy="modalClose" :mx="{ base: 'sm', md: 'md' }">
          {{ $t("events.back") }}
        </c-button>
        <c-button
          variation="solid"
          @click="jumpToLyyti(selectedEvent)"
          background="brand.beige"
          borderColor="brand.beige"
          :mx="{ base: 'sm', md: 'md' }"
        >
          {{ $t("events.enroll") }}
        </c-button>
      </template>
    </dialog-modal>
  </c-box>
</template>

<script lang="ts">
import Modal from "@/components/Modal.vue";
import { IEventsState } from "@/store/eventsModule";
import {
  CBox,
  CButton,
  CButtonGroup,
  CFlex,
  CHeading,
  CIcon,
  CModal,
  CSelect,
  CSwitch,
  CText,
} from "@chakra-ui/vue";
import { Event, EventCategory } from "@iloq/shared";
import moment from "moment-timezone";
import { defineComponent } from "vue";
import { mapState } from "vuex";

export default defineComponent({
  name: "Events",
  components: {
    "dialog-modal": Modal,
    CHeading,
    CBox,
    CFlex,
    CSelect,
    CButtonGroup,
    CButton,
    CText,
    CModal,
    CIcon,
    CSwitch,
  },
  computed: {
    ...mapState("events", {
      eventGroups: (state: unknown) => (state as IEventsState).filtered,
      filters: (state: unknown) => (state as IEventsState).filters,
      cities: (state: unknown) => (state as IEventsState).cities,
    }),
  },
  data() {
    return {
      EventCategory,
      selectedEvent: null as Event | null,
    };
  },
  methods: {
    updateCategoryFilter(category: EventCategory | undefined): void {
      this.$store.dispatch("events/updateCategoryFilter", category);
    },
    updateCityFilter(city: string | undefined): void {
      if (!city) {
        this.$store.dispatch("events/updateCityFilter", undefined);
        return;
      }
      this.$store.dispatch("events/updateCityFilter", city);
    },
    showEventDetails(event: Event): void {
      this.selectedEvent = event;
    },
    hideEventDetails(): void {
      this.selectedEvent = null;
    },
    isClientTimezone(timezoneName: string): boolean {
      return moment.tz.guess() === timezoneName;
    },
    jumpToLyyti(event: Event): void {
      window.open(event.enrollmentUrl, "_blank");
    },
  },
});
</script>
